import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { InscricaoComplemento } from 'src/app/models/inscricao-complemento'
import { MatDialog } from '@angular/material/dialog'
import { ArquivoViewerAvaliacaoComponent } from '../arquivo-viewer-avaliacao/arquivo-viewer-avaliacao.component'
import { DownloadService } from 'src/app/services/download.service'
import { firstValueFrom } from 'rxjs'
import { environment } from 'src/environments/environment'
import { Subscription } from 'rxjs'
import { ComissaoNotaProvaInscricaoRequest } from 'src/app/models/ComissaoNotaProvaInscricaoRequest'
import { ProvaExaminador } from 'src/app/models/prova-examinador.model'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-visualizar-complementos',
  templateUrl: './visualizar-complementos.component.html',
  styleUrls: ['./visualizar-complementos.component.scss'],
})
export class VisualizarComplementosComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef)

  inscricaoId!: number
  nomeInscricao: string = ''
  nomeSocialInscricao: string = ''
  codigoOrgao: string = ''
  complementos: InscricaoComplemento[] = []
  mapGruposComplementos: Map<string, InscricaoComplemento[]> = new Map()
  downloadingDocuments: Set<number> = new Set()
  downloadProgress: Map<number, number> = new Map()
  formComplementar = new FormGroup({
    inscricao: new FormGroup({}),
    segundaFase: new FormGroup({}),
  })

  nota: number | null = null
  envelopeEnviado: boolean = false
  idConcurso!: number
  tipoProva!: string
  pesoProva!: number
  descricaoProva!: string

  routeSubscription!: Subscription
  paramSubscription!: Subscription

  constructor(
    private readonly route: ActivatedRoute,
    private readonly unespCoreMessageService: UnespCoreMessageService,
    private readonly comissaoJulgadoraService: ComissaoJulgadoraService,
    private readonly dialog: MatDialog,
    private readonly downloadService: DownloadService
  ) {}

  ngOnInit(): void {
    this.routeSubscription = this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.inscricaoId = Number(params['inscricaoId'])
    })

    this.paramSubscription = this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.nota = Number(params.get('nota'))
      this.envelopeEnviado = params.get('envelopeEnviado') === 'true' // JS man
      this.idConcurso = Number(params.get('idConcurso'))
      this.tipoProva = params.get('tipoProva') ?? ''
      this.pesoProva = Number(params.get('peso'))
      this.loadComplementos()
      this.loadProvaAtual()
    })
  }

  private loadProvaAtual(): void {
    this.comissaoJulgadoraService.listarProvasExaminador(this.idConcurso).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (provas: ProvaExaminador[]) => {
        const provaEmAndamento = provas.find((p: ProvaExaminador) => p.emAndamento)
        if (provaEmAndamento) {
          this.descricaoProva = provaEmAndamento.tipoProva
        }
      },
      error: () => {
        this.descricaoProva = ''
      }
    })
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe()
    this.paramSubscription.unsubscribe()
  }

  private loadComplementos(): void {
    this.comissaoJulgadoraService.listarDocumentosComplementares(this.inscricaoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        this.complementos = response.inscricaoComplemento
        this.nomeInscricao = this.complementos.length > 0 ? this.complementos[0].nomeInscricao || '' : ''
        this.nomeSocialInscricao = this.complementos.length > 0 ? this.complementos[0].nomeSocialInscricao || '' : ''
        this.initComplementos(this.complementos)
      },
      error: err => {
        this.unespCoreMessageService.showMessageError(
          err?.error?.message ?? 'Erro ao carregar documentos complementares.'
        )
      },
    })
  }

  private initComplementos(complementos: InscricaoComplemento[]): void {
    this.complementos = complementos
    for (let complemento of complementos) {
      let grupoNome = '[' + complemento.etapa + '] ' + complemento.grupo
      let arr = this.mapGruposComplementos.get(grupoNome)
      arr ? arr.push(complemento) : this.mapGruposComplementos.set(grupoNome, [complemento])

      // if (!this.codigoOrgao && complemento.orgao) {
      //   this.codigoOrgao = complemento.orgao;
      // }
    }

    // Desabilitar formulário para modo somente leitura
    this.formComplementar.disable()
  }

  // usado para exibir na tela seguindo a ordem que os elementos foram adicionado no Map
  asIsOrder(a: any, b: any) {
    return 0
  }

  getTipoComplementoDescricao(tipo: string): string {
    const tipos: { [key: string]: string } = {
      TEXTO: 'Texto',
      ARQUIVO: 'Arquivo',
      FILE: 'Arquivo',
      CHECKBOX: 'Checkbox',
      GRUPO: 'Grupo',
    }
    return tipos[tipo] || tipo
  }

  async visualizarDocumento(complemento: InscricaoComplemento): Promise<void> {
    const complementoId = Number(complemento.id)

    if (this.downloadingDocuments.has(complementoId)) return

    this.downloadingDocuments.add(complementoId)
    this.downloadProgress.set(complementoId, 0)

    try {
      const usarDownloadChunks = !!environment.usarDownloadChunks

      const response = await firstValueFrom(
        this.comissaoJulgadoraService.downloadDocumentoComplementar(this.inscricaoId, complementoId, usarDownloadChunks)
      )

      const titulo = `${complemento.grupo} - Inscrição #${this.inscricaoId}`

      if (usarDownloadChunks) {
        const url = await this.downloadService.getObjectURLViaChunks(response, progress => {
          this.downloadProgress.set(complementoId, progress)
        })

        this.dialog.open(ArquivoViewerAvaliacaoComponent, {
          data: {
            tituloDoArquivo: titulo,
            urlDoArquivo: url,
            inscricaoId: this.inscricaoId,
            documentoId: complementoId,
            tipoProva: this.tipoProva,
            mostrarAnotacoes: true,
            envelopeEnviado: this.envelopeEnviado,
          },
          width: '98vw',
          height: '96vh',
          maxWidth: '100vw',
          panelClass: 'fullheight-dialog',
        })
      } else {
        const url = URL.createObjectURL(response)

        this.dialog.open(ArquivoViewerAvaliacaoComponent, {
          data: {
            tituloDoArquivo: titulo,
            urlDoArquivo: url,
            inscricaoId: this.inscricaoId,
            documentoId: complementoId,
            tipoProva: this.tipoProva,
            mostrarAnotacoes: true,
            envelopeEnviado: this.envelopeEnviado,
          },
          width: '98vw',
          height: '96vh',
          maxWidth: '100vw',
          panelClass: 'fullheight-dialog',
        })
      }
    } catch (error) {
      this.unespCoreMessageService.showMessageError('Erro ao carregar documento. Verifique se o arquivo existe.')
      console.error('Erro no download:', error)
    } finally {
      this.downloadingDocuments.delete(complementoId)
      this.downloadProgress.delete(complementoId)
    }
  }

  isDownloading(complemento: InscricaoComplemento): boolean {
    const complementoId = Number(complemento.id)
    return this.downloadingDocuments.has(complementoId)
  }

  getDownloadProgress(complemento: InscricaoComplemento): number {
    const complementoId = Number(complemento.id)
    return this.downloadProgress.get(complementoId) || 0
  }

  voltar(): void {
    window.close()
  }

  onNotaConfirmation(nota: number) {
    const request: ComissaoNotaProvaInscricaoRequest = {
      inscricaoId: this.inscricaoId.toString(), // Don't ask me
      concursoId: this.idConcurso,
      tipoProva: this.tipoProva,
      nota: nota,
    }

    this.comissaoJulgadoraService.registrarNota(request).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.unespCoreMessageService.showMessageSuccess('Nota Salva')
      },
      error: e => {
        this.unespCoreMessageService.showMessageError('Ocorreu um erro ao salvar a nota: ' + e.error.message)
      },
    })
  }
}
